import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiRotateCw, FiZap, FiCheck, FiArrowDown } = FiIcons;

function CoffeeGrinder({ onGrindComplete, isActive, targetFineness = 'medium' }) {
  const [isGrinding, setIsGrinding] = useState(false);
  const [grindProgress, setGrindProgress] = useState(0);
  const [tapCount, setTapCount] = useState(0);
  const [fineness, setFineness] = useState('coarse');
  const [showInstructions, setShowInstructions] = useState(true);

  const finenessLevels = ['coarse', 'medium-coarse', 'medium', 'medium-fine', 'fine'];
  const targetIndex = finenessLevels.indexOf(targetFineness);

  useEffect(() => {
    if (isActive) {
      setShowInstructions(true);
      const timer = setTimeout(() => setShowInstructions(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [isActive]);

  useEffect(() => {
    if (tapCount > 0) {
      // Calculate fineness based on tap count
      const progressRatio = Math.min(tapCount / 50, 1); // 50 taps for finest grind
      const currentIndex = Math.floor(progressRatio * (finenessLevels.length - 1));
      setFineness(finenessLevels[currentIndex]);
      setGrindProgress(progressRatio * 100);

      // Check if we've reached the target fineness
      if (currentIndex >= targetIndex && tapCount >= 20) {
        handleGrindComplete();
      }
    }
  }, [tapCount, targetIndex]);

  const handleTap = () => {
    if (!isActive) return;

    // Haptic feedback
    if (navigator.vibrate) {
      navigator.vibrate(30);
    }

    setTapCount(prev => prev + 1);
    setIsGrinding(true);

    // Stop grinding animation after short delay
    setTimeout(() => setIsGrinding(false), 200);
  };

  const handleGrindComplete = () => {
    if (onGrindComplete) {
      const quality = calculateGrindQuality();
      onGrindComplete({
        fineness,
        quality,
        tapCount
      });
    }
  };

  const calculateGrindQuality = () => {
    const currentIndex = finenessLevels.indexOf(fineness);
    const difference = Math.abs(currentIndex - targetIndex);
    
    if (difference === 0) return 'perfect';
    if (difference === 1) return 'good';
    if (difference === 2) return 'okay';
    return 'poor';
  };

  const getFinenessColor = (level) => {
    const colors = {
      'coarse': 'from-amber-800 to-amber-900',
      'medium-coarse': 'from-amber-700 to-amber-800',
      'medium': 'from-amber-600 to-amber-700',
      'medium-fine': 'from-amber-500 to-amber-600',
      'fine': 'from-amber-400 to-amber-500'
    };
    return colors[level] || colors.medium;
  };

  const getQualityColor = () => {
    const quality = calculateGrindQuality();
    const colors = {
      'perfect': 'text-green-500',
      'good': 'text-blue-500',
      'okay': 'text-yellow-500',
      'poor': 'text-red-500'
    };
    return colors[quality] || colors.okay;
  };

  if (!isActive) return null;

  return (
    <motion.div
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <motion.div
        className="bg-white rounded-3xl p-6 shadow-2xl max-w-sm w-full"
        initial={{ scale: 0.8, y: 50 }}
        animate={{ scale: 1, y: 0 }}
      >
        {/* Header */}
        <div className="text-center mb-6">
          <div className="text-4xl mb-2">⚙️</div>
          <h3 className="text-xl font-bold text-gray-800">Coffee Grinder</h3>
          <p className="text-sm text-gray-600">Tap rapidly to grind beans</p>
        </div>

        {/* Instructions */}
        <AnimatePresence>
          {showInstructions && (
            <motion.div
              className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
            >
              <div className="flex items-center text-blue-700">
                <SafeIcon icon={FiArrowDown} className="mr-2 animate-bounce" />
                <span className="text-sm font-medium">
                  Tap the grinder rapidly for {targetFineness} grind
                </span>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Grinder Visual */}
        <div className="relative mb-6">
          <motion.div
            className="w-32 h-32 mx-auto bg-gradient-to-b from-gray-700 to-gray-900 rounded-full relative overflow-hidden shadow-xl cursor-pointer select-none"
            whileTap={{ scale: 0.95 }}
            animate={isGrinding ? { rotate: [0, 15, -15, 0] } : {}}
            transition={{ duration: 0.2 }}
            onClick={handleTap}
            onTouchStart={handleTap}
          >
            {/* Grinder blades */}
            <motion.div
              className="absolute inset-4 bg-gradient-to-r from-gray-400 to-gray-600 rounded-full flex items-center justify-center"
              animate={isGrinding ? { rotate: 360 } : {}}
              transition={{ duration: 0.1, ease: "linear" }}
            >
              <SafeIcon icon={FiRotateCw} className="text-2xl text-gray-200" />
            </motion.div>

            {/* Coffee grounds particles */}
            <AnimatePresence>
              {isGrinding && (
                <>
                  {[...Array(8)].map((_, i) => (
                    <motion.div
                      key={i}
                      className={`absolute w-1 h-1 bg-gradient-to-r ${getFinenessColor(fineness)} rounded-full`}
                      initial={{
                        x: 64,
                        y: 64,
                        scale: 0
                      }}
                      animate={{
                        x: 64 + (Math.random() - 0.5) * 100,
                        y: 64 + (Math.random() - 0.5) * 100,
                        scale: [0, 1, 0]
                      }}
                      transition={{
                        duration: 0.5,
                        delay: i * 0.02
                      }}
                    />
                  ))}
                </>
              )}
            </AnimatePresence>
          </motion.div>

          {/* Tap indicator */}
          <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2">
            <div className="bg-amber-500 text-white px-3 py-1 rounded-full text-xs font-bold">
              Tap to Grind!
            </div>
          </div>
        </div>

        {/* Progress and Stats */}
        <div className="space-y-4">
          {/* Grind Progress */}
          <div>
            <div className="flex justify-between text-sm mb-1">
              <span className="text-gray-600">Grinding Progress</span>
              <span className="font-medium">{Math.round(grindProgress)}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3">
              <motion.div
                className="bg-gradient-to-r from-amber-500 to-orange-500 h-3 rounded-full relative overflow-hidden"
                style={{ width: `${grindProgress}%` }}
                transition={{ duration: 0.3 }}
              >
                {isGrinding && (
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-pulse" />
                )}
              </motion.div>
            </div>
          </div>

          {/* Current Fineness */}
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-600">Current Fineness:</span>
            <span className={`font-bold capitalize ${getQualityColor()}`}>
              {fineness.replace('-', ' ')}
            </span>
          </div>

          {/* Target vs Current */}
          <div className="bg-gray-50 rounded-lg p-3">
            <div className="flex justify-between text-xs mb-2">
              <span className="text-gray-500">Target:</span>
              <span className="font-medium capitalize">{targetFineness}</span>
            </div>
            <div className="flex justify-between text-xs">
              <span className="text-gray-500">Taps:</span>
              <span className="font-medium">{tapCount}</span>
            </div>
          </div>

          {/* Quality Indicator */}
          {tapCount > 10 && (
            <motion.div
              className={`text-center p-2 rounded-lg ${
                calculateGrindQuality() === 'perfect' ? 'bg-green-100 text-green-700' :
                calculateGrindQuality() === 'good' ? 'bg-blue-100 text-blue-700' :
                calculateGrindQuality() === 'okay' ? 'bg-yellow-100 text-yellow-700' :
                'bg-red-100 text-red-700'
              }`}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
            >
              <div className="font-bold capitalize">
                {calculateGrindQuality()} Grind!
              </div>
            </motion.div>
          )}

          {/* Complete Button */}
          {grindProgress >= 40 && (
            <motion.button
              className="w-full bg-gradient-to-r from-green-500 to-green-600 text-white py-3 rounded-xl font-bold"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleGrindComplete}
            >
              <SafeIcon icon={FiCheck} className="inline mr-2" />
              Use This Grind
            </motion.button>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}

export default CoffeeGrinder;